/**
 * Created by neo on 11.01.2026
 * Activity Log Form Modal - Form for manually logging activities
 */
import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useSaveActivityLog } from '../../../Hooks/useActivityLogMutations';
import { useToast } from '../../../Hooks/useToast';
import { ActivityLog, newActivityLog } from '../../../Model/Activity/ActivityLog';
import { Input } from '../../../Components/Input';
import { Button } from '../../../Components/Button';
import Select, { SingleValue } from 'react-select';
import { ACTIVITY_TYPES, ActivityType } from '../../../Config/ActivityTypes';
import { Theme } from '../../../Theme/Theme';

export type ActivityLogFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onActivityLogged?: (activity: ActivityLog) => void;
};

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 8px;
`;

const DurationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.error};
  margin: 4px 0 0 0;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin: 4px 0 0 0;
`;

export const ActivityLogFormModal: React.FC<ActivityLogFormModalProps> = observer(
  ({ isOpen, onClose, onActivityLogged }) => {
    const { t } = useTrans();
    const { authentication, engagement } = useRootStore();
    const toast = useToast();
    const saveActivityLog = useSaveActivityLog();

    const [selectedType, setSelectedType] = useState<ActivityType | null>(null);
    const [startDateTime, setStartDateTime] = useState(() => dayjs().format('YYYY-MM-DDTHH:mm'));
    const [hours, setHours] = useState('0');
    const [minutes, setMinutes] = useState('30');
    const [distance, setDistance] = useState('');
    const [calories, setCalories] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const activityTypeOptions = useMemo(
      () =>
        ACTIVITY_TYPES.map((type) => ({
          value: type.id,
          label: `${type.icon} ${type.name}`,
        })),
      [],
    );

    const showDistanceField = useMemo(() => selectedType?.metrics.includes('distance'), [selectedType]);

    const showCaloriesField = useMemo(() => selectedType?.metrics.includes('calories'), [selectedType]);

    const handleClose = useCallback(() => {
      if (!isLoading) {
        setSelectedType(null);
        setStartDateTime(dayjs().format('YYYY-MM-DDTHH:mm'));
        setHours('0');
        setMinutes('30');
        setDistance('');
        setCalories('');
        setErrors({});
        onClose();
      }
    }, [isLoading, onClose]);

    const validate = useCallback((): boolean => {
      const newErrors: Record<string, string> = {};

      if (!selectedType) {
        newErrors.type = t('activity.log.error.typeRequired', {
          defaultValue: 'Please select an activity type',
        });
      }

      const totalMinutes = parseInt(hours || '0') * 60 + parseInt(minutes || '0');
      if (totalMinutes <= 0) {
        newErrors.duration = t('activity.log.error.durationRequired', {
          defaultValue: 'Duration must be greater than 0',
        });
      }

      if (showDistanceField && distance && parseFloat(distance) <= 0) {
        newErrors.distance = t('activity.log.error.invalidDistance', {
          defaultValue: 'Distance must be greater than 0',
        });
      }

      if (showCaloriesField && calories && parseFloat(calories) <= 0) {
        newErrors.calories = t('activity.log.error.invalidCalories', {
          defaultValue: 'Calories must be greater than 0',
        });
      }

      // Check if date is in the future
      const selectedDate = dayjs(startDateTime);
      if (selectedDate.isAfter(dayjs())) {
        newErrors.date = t('activity.log.error.futureDate', {
          defaultValue: 'Cannot log activities in the future',
        });
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [selectedType, hours, minutes, distance, calories, startDateTime, showDistanceField, showCaloriesField, t]);

    const handleSubmit = useCallback(async () => {
      if (!validate() || !selectedType || !authentication.athlete) return;

      setIsLoading(true);

      try {
        const totalMinutes = parseInt(hours || '0') * 60 + parseInt(minutes || '0');
        const startDate = dayjs(startDateTime).toDate();
        const endDate = dayjs(startDate).add(totalMinutes, 'minutes').toDate();

        const data: Record<string, number> = { DURATION: totalMinutes };

        // Set distance if applicable (in km)
        if (showDistanceField && distance) {
          data.DISTANCE = parseFloat(distance);
        }

        // Set calories if applicable
        if (showCaloriesField && calories) {
          data.CALORIES = parseFloat(calories);
        }

        const activityLog = newActivityLog({
          athleteId: authentication.athlete.id,
          type: selectedType.id,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          source: {
            source: 'kinastic',
            sourceType: 'activity',
            sourceId: crypto.randomUUID(),
          },
          data,
        });

        // Save activity
        const savedActivity = await saveActivityLog.mutateAsync(activityLog);

        // Send gamification event
        await engagement.sendActivityLogged(savedActivity);

        toast.showToast(t('activity.log.success', { defaultValue: 'Activity logged successfully!' }), 'success');

        if (onActivityLogged) {
          onActivityLogged(savedActivity);
        }

        handleClose();
      } catch (error) {
        console.error('Error logging activity:', error);
        toast.showToast(
          t('activity.log.error.failed', { defaultValue: 'Failed to log activity. Please try again.' }),
          'error',
        );
      } finally {
        setIsLoading(false);
      }
    }, [
      validate,
      selectedType,
      authentication.athlete,
      hours,
      minutes,
      startDateTime,
      showDistanceField,
      distance,
      showCaloriesField,
      calories,
      engagement,
      t,
      onActivityLogged,
      handleClose,
      saveActivityLog,
      toast,
    ]);

    return (
      <StyledModal isOpen={isOpen} toggle={handleClose} size="md" centered>
        <ModalHeader toggle={handleClose}>{t('activity.log.title', { defaultValue: 'Log Activity' })}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label htmlFor="activityType">{t('activity.log.typeLabel', { defaultValue: 'Activity Type' })} *</Label>
            <Select
              value={
                selectedType ? { value: selectedType.id, label: `${selectedType.icon} ${selectedType.name}` } : null
              }
              onChange={(option: SingleValue<{ value: string; label: string }>) => {
                const type = ACTIVITY_TYPES.find((t) => t.id === option?.value);
                setSelectedType(type || null);
              }}
              options={activityTypeOptions}
              placeholder={t('activity.log.typePlaceholder', { defaultValue: 'Select activity' })}
              isSearchable={true}
              isClearable={true}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: Theme.Colors.primary,
                  primary25: Theme.Colors.lightestGrey,
                },
              })}
            />
            {errors.type && <ErrorText>{errors.type}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="startDateTime">{t('activity.log.dateLabel', { defaultValue: 'Date & Time' })} *</Label>
            <Input
              id="startDateTime"
              type="datetime-local"
              value={startDateTime}
              onChange={(e) => setStartDateTime(e.target.value)}
              max={dayjs().format('YYYY-MM-DDTHH:mm')}
              disabled={isLoading}
            />
            {errors.date && <ErrorText>{errors.date}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>{t('activity.log.durationLabel', { defaultValue: 'Duration' })} *</Label>
            <DurationRow>
              <div>
                <Input
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min="0"
                  placeholder={t('activity.log.hoursPlaceholder', { defaultValue: 'Hours' })}
                  disabled={isLoading}
                />
              </div>
              <div>
                <Input
                  type="number"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  min="0"
                  max="59"
                  placeholder={t('activity.log.minutesPlaceholder', { defaultValue: 'Minutes' })}
                  disabled={isLoading}
                />
              </div>
            </DurationRow>
            {errors.duration && <ErrorText>{errors.duration}</ErrorText>}
          </FormGroup>

          {showDistanceField && (
            <FormGroup>
              <Label htmlFor="distance">{t('activity.log.distanceLabel', { defaultValue: 'Distance (km)' })}</Label>
              <Input
                id="distance"
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                min="0"
                step="0.1"
                placeholder={t('activity.log.distancePlaceholder', { defaultValue: 'e.g., 5.0' })}
                disabled={isLoading}
              />
              {errors.distance && <ErrorText>{errors.distance}</ErrorText>}
              <HelpText>{t('activity.log.distanceHint', { defaultValue: 'Optional' })}</HelpText>
            </FormGroup>
          )}

          {showCaloriesField && (
            <FormGroup>
              <Label htmlFor="calories">{t('activity.log.caloriesLabel', { defaultValue: 'Calories' })}</Label>
              <Input
                id="calories"
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                min="0"
                step="1"
                placeholder={t('activity.log.caloriesPlaceholder', { defaultValue: 'e.g., 250' })}
                disabled={isLoading}
              />
              {errors.calories && <ErrorText>{errors.calories}</ErrorText>}
              <HelpText>{t('activity.log.caloriesHint', { defaultValue: 'Optional' })}</HelpText>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('activity.log.logging', { defaultValue: 'Logging...' })
              : t('activity.log.submit', { defaultValue: 'Log Activity' })}
          </Button>
        </ModalFooter>
      </StyledModal>
    );
  },
);
